import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import type { InstitutionFuturesClientCategorisation } from '@/features/institution/futures/client-categorisation/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import {
  institutionDetailsClientCategorisationAdditionalSchema,
  institutionDetailsClientCategorisationGeneralSchema,
  institutionDetailsClientCategorisationSchema,
} from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { InstitutionFuturesClientCategorisationFieldsetAdditional, InstitutionFuturesClientCategorisationFieldsetAdditionalLegend } from './fieldsets/additional';
import { InstitutionFuturesClientCategorisationFieldsetGeneral, InstitutionFuturesClientCategorisationFieldsetGeneralLegend } from './fieldsets/general';

const FORM_STEPS = {
  GENERAL: {
    Component: InstitutionFuturesClientCategorisationFieldsetGeneral,
    Legend: InstitutionFuturesClientCategorisationFieldsetGeneralLegend,
    schema: institutionDetailsClientCategorisationGeneralSchema,
  },
  ADDITIONAL: {
    Component: InstitutionFuturesClientCategorisationFieldsetAdditional,
    Legend: InstitutionFuturesClientCategorisationFieldsetAdditionalLegend,
    schema: institutionDetailsClientCategorisationAdditionalSchema,
  },
} satisfies Record<string, FormStep<InstitutionFuturesClientCategorisation>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionFuturesClientCategorisationOnboardingFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionFuturesClientCategorisation>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'clientCategorisation', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionFuturesClientCategorisationOnboardingFormCurrentStep.displayName = 'InstitutionFuturesClientCategorisationOnboardingFormCurrentStep';

const InstitutionFuturesClientCategorisationOnboardingFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionFuturesClientCategorisation>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionFuturesClientCategorisationOnboardingFormCurrentStepLegend.displayName = 'InstitutionFuturesClientCategorisationOnboardingFormCurrentStepLegend';

const InstitutionFuturesClientCategorisationOnboardingFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionFuturesClientCategorisation>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/execution`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, navigate, prevStep, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionFuturesClientCategorisationOnboardingFormBackButton.displayName = 'InstitutionFuturesClientCategorisationOnboardingFormBackButton';

const InstitutionFuturesClientCategorisationOnboardingFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionFuturesClientCategorisation>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionFuturesClientCategorisationOnboardingFormFooter.displayName = 'InstitutionFuturesClientCategorisationOnboardingFormFooter';

const InstitutionFuturesClientCategorisationOnboardingForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'clientCategorisation');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionFuturesClientCategorisation, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    await mutateAsync(
      { clientCategorisation: await institutionDetailsClientCategorisationSchema.parseAsync(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/reporting`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.clientCategorisation ?? {};

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionFuturesClientCategorisationOnboardingFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionFuturesClientCategorisationOnboardingFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionFuturesClientCategorisation) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionFuturesClientCategorisationOnboardingFormCurrentStep />
          <InstitutionFuturesClientCategorisationOnboardingFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionFuturesClientCategorisationOnboardingForm.displayName = 'InstitutionFuturesClientCategorisationOnboardingForm';

export default InstitutionFuturesClientCategorisationOnboardingForm;
